'use client';

import './sac-card.scss';

import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.js';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';

import { Image } from '../../atoms/image/image';

type SacCardProps = ChildAvailabilityItem & {
  selectedChild?: string;
  setSelectedChild?: (childId: string) => void;
};

export const SacCard: React.FC<SacCardProps> = ({
  name: { preferred: preferredName },
  imageUrl,
  birthDate,
  icp: { country },
  minDaysWaiting,
  age,
  globalId,
  sourceCode,
  selectedChild,
  setSelectedChild,
}) => {
  const { t } = useTranslation(['children']);
  const router = useRouter();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setSelectedChild?.(globalId);

    e.preventDefault();
    sessionStorage.setItem('sacSelectedChild', globalId);
    router.push(
      `/sponsor-a-child/child-bio?gid=${globalId}&source=${sourceCode}`,
    );
    // Force refresh since layout doesn’t re-render on searchParams change under Suspense.
    // TODO: Revisit once layout handles this properly.
    router.refresh();
  };

  const imageURLTransform =
    imageUrl?.replace('w_150', 'f_auto,q_auto,c_scale,w_500') || undefined;

  return (
    <div className="flex flex-col relative">
      <Link
        href={`/sponsor-a-child/child-bio?gid=${globalId}&source=${sourceCode}`}
        className="SacCard__bio-link"
        onClick={handleClick}
        title={preferredName}
      >
        <div className="relative">
          <div className="SacCard__badge absolute m-comfy-xs">
            <Badge
              color="brand"
              label={t('children:child.waiting', { numDays: minDaysWaiting })}
            />
          </div>
          <div className="SacCard__child-image-container">
            {/* TODO: Should use ChildImage component - that's where we want to isolate transform logic */}
            <Image
              image={
                imageURLTransform
                  ? { url: imageURLTransform, alt: preferredName }
                  : undefined
              }
              loader={loader}
              sizeIntent="third"
              className="SacCard__child-image"
              rounded="lg"
              hasPlaceholder
            />
          </div>
          {selectedChild === globalId && (
            <div className="SacCard__overlay rounded-lg flex flex-col">
              <div className="SacCard__spinner mb-comfy-sm" />
              <span className="body body-md SacCard__overlay-text">
                {t('children:child.loading')}
              </span>
            </div>
          )}
        </div>
        <div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">
          <span className="SacCard__name heading heading-sm">
            {preferredName}
          </span>
          <div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs body body-md">
            <span className=" SacCard__glyph-text flex gap-comfy-xs items-center">
              <ArkIcon iconName="birthday-cake" />
              {t('children:sac.age', {
                count: Number(age),
                birthday: birthDate,
              })}
            </span>
            {country && (
              <span className="SacCard__glyph-text flex gap-comfy-xs items-center">
                <ArkIcon iconName="globe-05" />
                {country}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export const SacCardSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col">
      <div className="relative">
        <div className="absolute m-comfy-xs z-10">
          <Skeleton height={24} width={100} borderRadius="9999px" />
        </div>

        <Skeleton
          width="100%"
          height="auto"
          style={{ aspectRatio: '3/4' }}
          borderRadius="1em"
        />
      </div>

      <div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">
        <span className="SacCard__name">
          <Skeleton height={24} width="70%" />
        </span>

        <div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs">
          <div className="SacCard__glyph-text flex gap-comfy-xs items-center">
            <Skeleton height={20} width={20} borderRadius="0.25rem" />
            <Skeleton height={20} width={180} />
          </div>
          <div className="SacCard__glyph-text flex gap-comfy-xs items-center">
            <Skeleton height={20} width={20} borderRadius="0.25rem" />
            <Skeleton height={20} width={150} />
          </div>
        </div>
      </div>
    </div>
  );
};
